import React, { useState, useMemo, useCallback } from 'react';
import { BsChevronRight, BsChevronDown } from 'react-icons/bs';

const toDateKey = (val) => {
    if (!val) return null;
    const d = new Date(val);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatColHeader = (dateKey) => {
    const parts = dateKey.split('-');
    return `${parts[2]}/${parts[1]}`;
};

const getHeatColour = (pct) => {
    if (pct === null) return 'transparent';
    const r = pct < 50 ? 180 : Math.round(180 - (pct - 50) * 2.8);
    const g = pct > 50 ? 150 : Math.round(pct * 2.4);
    const b = pct > 80 ? 30 : 0;
    return `rgba(${r}, ${g}, ${b}, 0.7)`;
};

const getDatesBetween = (startStr, endStr) => {
    const dates = [];
    const current = new Date(startStr);
    const end = new Date(endStr);
    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    while (current <= end) {
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        dates.push(`${y}-${m}-${d}`);
        current.setDate(current.getDate() + 1);
    }
    return dates;
};

const calcPct = (tasks) => {
    if (!tasks || tasks.length === 0) return null;
    const completed = tasks.filter(t => t.taskStatus === 'Completed' || t.taskStatus === 'Completed Late').length;
    return Math.round((completed / tasks.length) * 100);
};

const buildLookup = (data) => {
    const lookup = {};

    data.forEach(row => {
        const site = row.siteName || 'Unknown';
        const task = row.taskDescription || 'Unknown Task';
        const staff = row.staffName || 'Unassigned';
        const dateKey = toDateKey(row.dueDate);
        if (!dateKey) return;

        const keys = [
            `site:${site}:${dateKey}`,
            `task:${site}:${task}:${dateKey}`,
            `staff:${site}:${task}:${staff}:${dateKey}`,
        ];

        keys.forEach(k => {
            if (!lookup[k]) lookup[k] = [];
            lookup[k].push(row);
        });
    });

    return lookup;
};

const buildTree = (data) => {
    const siteMap = {};

    data.forEach(row => {
        const site = row.siteName || 'Unknown';
        const task = row.taskDescription || 'Unknown Task';
        const staff = row.staffName || 'Unassigned';

        if (!siteMap[site]) siteMap[site] = {};
        if (!siteMap[site][task]) siteMap[site][task] = new Set();
        siteMap[site][task].add(staff);
    });

    return siteMap;
};

const isRowHighlighted = (filter, { site, task, staff }) => {
    if (!filter) return false;
    if (filter.date) return false;
    if (filter.site && filter.site !== site) return false;
    if (filter.task && filter.task !== task) return false;
    if (filter.staff && filter.staff !== staff) return false;
    return true;
};

const isCellHighlighted = (filter, { site, task, staff, date }) => {
    if (!filter || !filter.date) return false;
    if (filter.site && filter.site !== site) return false;
    if (filter.task && filter.task !== task) return false;
    if (filter.staff && filter.staff !== staff) return false;
    return filter.date === date;
};

const isDateHighlighted = (filter, date) => {
    return !!filter && filter.date === date;
};

const shouldFilterHeatmap = (filter) => {
    return filter && (filter.source === 'detail' || filter.source === 'chart');
};

const applyHeatmapFilter = (data, filter) => {
    if (!filter) return data;
    return data.filter(row => {
        if (filter.site && row.siteName !== filter.site) return false;
        if (filter.task && row.taskDescription !== filter.task) return false;
        if (filter.staff && row.staffName !== filter.staff) return false;
        if (filter.status && row.taskStatus !== filter.status) return false;
        return true;
    });
};

const HeatmapTreeGrid = ({ data, siteFilter, dateRange, onCellClick, onRowClick, activeFilter }) => {
    const [expanded, setExpanded] = useState({});

    const filtered = useMemo(() => {
        if (!data || data.length === 0) return [];
        let result = siteFilter ? data.filter(d => d.siteName === siteFilter) : data;
        if (shouldFilterHeatmap(activeFilter)) {
            result = applyHeatmapFilter(result, activeFilter);
        }
        return result;
    }, [data, siteFilter, activeFilter]);

    const lookup = useMemo(() => buildLookup(filtered), [filtered]);
    const tree = useMemo(() => buildTree(filtered), [filtered]);

    const dateColumns = useMemo(() => {
        if (!dateRange) return [];
        return getDatesBetween(dateRange.from, dateRange.to);
    }, [dateRange]);

    const toggleExpand = useCallback((key) => {
        setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const sites = useMemo(() => Object.keys(tree).sort(), [tree]);

    const hasAnyExpanded = useMemo(() =>
        Object.keys(expanded).some(k => expanded[k] && k.startsWith('site:'))
    , [expanded]);

    const sitesWithExpandedTasks = useMemo(() => {
        const set = new Set();
        Object.keys(expanded).forEach(key => {
            if (expanded[key] && key.startsWith('task:')) {
                set.add(key.split(':')[1]);
            }
        });
        return set;
    }, [expanded]);

    if (sites.length === 0 || dateColumns.length === 0) {
        return <div className="heatmap-grid__empty">No data available</div>;
    }

    const heatmapIsFiltered = shouldFilterHeatmap(activeFilter);

    const renderPctCell = (lookupKey, dateKey, clickPayload, dimmed, highlightCtx) => {
        const tasks = lookup[`${lookupKey}:${dateKey}`];
        const pct = calcPct(tasks);
        const empty = pct === null;

        const cellActive = !heatmapIsFiltered && isCellHighlighted(activeFilter, { ...highlightCtx, date: dateKey });
        const dateActive = !heatmapIsFiltered && isDateHighlighted(activeFilter, dateKey);
        const rowActive = !heatmapIsFiltered && isRowHighlighted(activeFilter, highlightCtx);

        const highlighted = cellActive || rowActive || dateActive;
        const hasFilter = !!activeFilter && !heatmapIsFiltered;

        return (
            <td
                key={dateKey}
                className={[
                    'heatmap-grid__pct-cell',
                    dimmed ? 'heatmap-grid__pct-cell--dim' : '',
                    empty ? 'heatmap-grid__pct-cell--empty' : '',
                    cellActive ? 'heatmap-grid__pct-cell--cell-active' : '',
                    rowActive ? 'heatmap-grid__pct-cell--row-active' : '',
                    dateActive && !cellActive ? 'heatmap-grid__pct-cell--col-active' : '',
                    hasFilter && !highlighted && !dimmed && !empty ? 'heatmap-grid__pct-cell--faded' : '',
                ].filter(Boolean).join(' ')}
                style={empty ? undefined : { backgroundColor: getHeatColour(pct) }}
                onClick={(e) => { if (pct !== null) { e.stopPropagation(); onCellClick?.(clickPayload); } }}
                title={pct !== null ? `${pct}% (${tasks.length} tasks)` : ''}
            >
                {pct !== null ? `${pct}%` : ''}
            </td>
        );
    };

    const renderRowAvg = (lookupPrefix, dimmed) => {
        let totalPct = 0;
        let count = 0;
        dateColumns.forEach(dateKey => {
            const tasks = lookup[`${lookupPrefix}:${dateKey}`];
            const pct = calcPct(tasks);
            if (pct !== null) {
                totalPct += pct;
                count++;
            }
        });
        const avg = count > 0 ? Math.round(totalPct / count) : null;
        return (
            <td
                className={`heatmap-grid__pct-cell heatmap-grid__avg-cell heatmap-grid__col-total${dimmed ? ' heatmap-grid__pct-cell--dim' : ''}`}
            >
                {avg !== null ? `${avg}%` : ''}
            </td>
        );
    };

    const renderColumnTotals = () => {
        return dateColumns.map(dateKey => {
            let totalPct = 0;
            let count = 0;
            sites.forEach(site => {
                const tasks = lookup[`site:${site}:${dateKey}`];
                const pct = calcPct(tasks);
                if (pct !== null) {
                    totalPct += pct;
                    count++;
                }
            });
            const avg = count > 0 ? Math.round(totalPct / count) : null;
            return (
                <td key={dateKey} className="heatmap-grid__pct-cell heatmap-grid__total-cell">
                    {avg !== null ? `${avg}%` : ''}
                </td>
            );
        });
    };

    const renderTotalRowAvg = () => {
        let grandTotal = 0;
        let grandCount = 0;
        sites.forEach(site => {
            dateColumns.forEach(dateKey => {
                const tasks = lookup[`site:${site}:${dateKey}`];
                const pct = calcPct(tasks);
                if (pct !== null) {
                    grandTotal += pct;
                    grandCount++;
                }
            });
        });
        const avg = grandCount > 0 ? Math.round(grandTotal / grandCount) : null;
        return (
            <td className="heatmap-grid__pct-cell heatmap-grid__total-cell heatmap-grid__col-total">
                {avg !== null ? `${avg}%` : ''}
            </td>
        );
    };

    return (
        <div className="heatmap-grid">
            <div className="heatmap-grid__scroll">
                <table className="heatmap-grid__table">
                    <thead>
                        <tr>
                            <th className="heatmap-grid__name-col">Site</th>
                            {dateColumns.map(d => (
                                <th
                                    key={d}
                                    className={`heatmap-grid__date-header${!heatmapIsFiltered && isDateHighlighted(activeFilter, d) ? ' heatmap-grid__date-header--active' : ''}`}
                                    onClick={() => onCellClick?.({ date: d })}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {formatColHeader(d)}
                                </th>
                            ))}
                            <th className="heatmap-grid__date-header heatmap-grid__col-total">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sites.map(site => {
                            const siteKey = `site:${site}`;
                            const siteExpanded = !!expanded[siteKey];
                            const tasks = Object.keys(tree[site]).sort();
                            const siteDimmed = hasAnyExpanded && !siteExpanded;
                            const siteHasExpandedTask = sitesWithExpandedTasks.has(site);
                            const siteRowActive = !heatmapIsFiltered && isRowHighlighted(activeFilter, { site });

                            return (
                                <React.Fragment key={siteKey}>
                                    <tr
                                        className={[
                                            'heatmap-grid__site-row',
                                            siteDimmed ? 'heatmap-grid__row--dim' : '',
                                            siteRowActive ? 'heatmap-grid__row--active' : '',
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => onRowClick?.({ site })}
                                    >
                                        <td className="heatmap-grid__name-cell heatmap-grid__name-cell--site">
                                            <button
                                                className="heatmap-grid__expand-btn"
                                                onClick={(e) => { e.stopPropagation(); toggleExpand(siteKey); }}
                                            >
                                                {siteExpanded ? <BsChevronDown size={10} /> : <BsChevronRight size={10} />}
                                            </button>
                                            <span>{site}</span>
                                        </td>
                                        {dateColumns.map(dateKey =>
                                            renderPctCell(`site:${site}`, dateKey, { site, date: dateKey }, siteDimmed, { site })
                                        )}
                                        {renderRowAvg(`site:${site}`, siteDimmed)}
                                    </tr>

                                    {siteExpanded && tasks.map(task => {
                                        const taskKey = `task:${site}:${task}`;
                                        const taskExpanded = !!expanded[taskKey];
                                        const staffMembers = [...tree[site][task]].sort();
                                        const taskDimmed = siteHasExpandedTask && !taskExpanded;
                                        const taskRowActive = !heatmapIsFiltered && isRowHighlighted(activeFilter, { site, task });

                                        return (
                                            <React.Fragment key={taskKey}>
                                                <tr
                                                    className={[
                                                        'heatmap-grid__task-row',
                                                        taskDimmed ? 'heatmap-grid__row--dim' : '',
                                                        taskRowActive ? 'heatmap-grid__row--active' : '',
                                                    ].filter(Boolean).join(' ')}
                                                    onClick={() => onRowClick?.({ site, task })}
                                                >
                                                    <td className="heatmap-grid__name-cell heatmap-grid__name-cell--task">
                                                        <button
                                                            className="heatmap-grid__expand-btn"
                                                            onClick={(e) => { e.stopPropagation(); toggleExpand(taskKey); }}
                                                        >
                                                            {taskExpanded ? <BsChevronDown size={9} /> : <BsChevronRight size={9} />}
                                                        </button>
                                                        <span>{task}</span>
                                                    </td>
                                                    {dateColumns.map(dateKey =>
                                                        renderPctCell(`task:${site}:${task}`, dateKey, { site, task, date: dateKey }, taskDimmed, { site, task })
                                                    )}
                                                    {renderRowAvg(`task:${site}:${task}`, taskDimmed)}
                                                </tr>

                                                {taskExpanded && staffMembers.map(staff => {
                                                    const staffRowActive = !heatmapIsFiltered && isRowHighlighted(activeFilter, { site, task, staff });

                                                    return (
                                                        <tr
                                                            key={`staff:${site}:${task}:${staff}`}
                                                            className={[
                                                                'heatmap-grid__staff-row',
                                                                staffRowActive ? 'heatmap-grid__row--active' : '',
                                                            ].filter(Boolean).join(' ')}
                                                            onClick={() => onRowClick?.({ site, task, staff })}
                                                        >
                                                            <td className="heatmap-grid__name-cell heatmap-grid__name-cell--staff">
                                                                <span>{staff}</span>
                                                            </td>
                                                            {dateColumns.map(dateKey =>
                                                                renderPctCell(`staff:${site}:${task}:${staff}`, dateKey, { site, task, staff, date: dateKey }, false, { site, task, staff })
                                                            )}
                                                            {renderRowAvg(`staff:${site}:${task}:${staff}`, false)}
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="heatmap-grid__total-row">
                            <td className="heatmap-grid__name-cell" style={{ fontWeight: 700 }}>Total</td>
                            {renderColumnTotals()}
                            {renderTotalRowAvg()}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default HeatmapTreeGrid;
